import React, { useState, useEffect, useRef } from "react";
import { postApi, getApi } from "../../Services/apiService";
import moment from 'moment';

const AddOfferModal = ({ setAddOfferModal, fetchOffers }) => {
  const [loader, setLoader] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    offer_code: "",
    description: "",
    expiry_date: "",
    category_id: "",
    image: null,
  });

  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [categorySearch, setCategorySearch] = useState("");
  const [isCategoryListOpen, setIsCategoryListOpen] = useState(false);
  const [error, setError] = useState({});
  const [filePreviews, setFilePreviews] = useState({ image: null });
  const [selectedCategoryId, setSelectedCategoryId] = useState(false);
  // Refs for form fields
  const titleRef = useRef(null);
  const offerCodeRef = useRef(null);
  const descriptionRef = useRef(null);
  const expiryDateRef = useRef(null);
  const categoryRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await getApi("/admin/category");
      if (response.status === 200) {
        setCategories(response?.data?.data);
        console.log("response.data", response?.data?.data);
        
        setFilteredCategories(response?.data?.data);
      }
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (error[name]) {
      setError({ ...error, [name]: '' });
    }
  };

  const handleCategorySearchChange = (e) => {
    const searchTerm = e.target.value;
    setCategorySearch(searchTerm);
    const filtered = categories.filter((cat) =>
      cat.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredCategories(filtered);
    setIsCategoryListOpen(true);
  };

  // const handleCategorySelect = (category) => {
  //   setFormData({ ...formData, category_id: category.id });
  //   setCategorySearch(category.name);
  //   setIsCategoryListOpen(false);
  //   if (error.category_id) {
  //     setError({ ...error, category_id: '' });
  //   }
  // };

  const handleCategorySelect = (category) => {
    setCategorySearch(category.name);
    setSelectedCategoryId(category.id);
    setFormData({ ...formData, category_id: category.id }); // Add this line
    setIsCategoryListOpen(false);
    if (error.category_id) {
          setError({ ...error, category_id: '' });
        }
};
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const previewURL = URL.createObjectURL(file);
      setFilePreviews({ ...filePreviews, image: previewURL });
      if (error.image) {
        setError({ ...error, image: '' });
      }
    }
  };

  const handleRemoveFile = () => {
    setFormData({ ...formData, image: null });
    setFilePreviews({ ...filePreviews, image: null });
  };

  const validateForm = () => {
    let formErrors = {};
    const rawTitle = formData.title || "";
    const title = rawTitle.trim().replace(/\s+/g, " ");
    if (!title) formErrors.title = "Title is required.";
    else if (title.length < 2 || title.length > 35) formErrors.title = "Title must be between 2 and 35 characters.";

    const rawDescription = formData.description || "";
    const description = rawDescription.trim();
    if (!description) formErrors.description = "Description is required.";
    else if (description.length < 2 || description.length > 250) formErrors.description = "Description must be between 2 and 250 characters.";

    const rawOfferCode = formData.offer_code || "";
    const offerCode = rawOfferCode.trim();
    if (!offerCode) formErrors.offer_code = "Offer code is required.";
    else if (offerCode.length < 2 || offerCode.length > 18) formErrors.offer_code = "Offer code must be between 2 and 18 characters.";

    if (!formData.expiry_date) formErrors.expiry_date = "Expiry date is required.";
    if (!formData.category_id) formErrors.category_id = "Category is required.";
    if (!formData.image) formErrors.image = "Image is required.";

    setError(formErrors);
    
    // Return both validation result and the first error field name
    const isValid = Object.keys(formErrors).length === 0;
    const firstErrorField = isValid ? null : Object.keys(formErrors)[0];
    
    return { isValid, firstErrorField };
  };

  const scrollToError = (fieldName) => {
    let elementToScroll = null;
    
    switch(fieldName) {
      case 'title': elementToScroll = titleRef.current; break;
      case 'offer_code': elementToScroll = offerCodeRef.current; break;
      case 'description': elementToScroll = descriptionRef.current; break;
      case 'expiry_date': elementToScroll = expiryDateRef.current; break;
      case 'category_id': elementToScroll = categoryRef.current; break;
      case 'image': elementToScroll = imageRef.current; break;
    }
    
    if (elementToScroll) {
      // Scroll the modal body to the error field
      const modalBody = document.querySelector('.scrollable-modal-body');
      const elementRect = elementToScroll.getBoundingClientRect();
      const absoluteElementTop = elementRect.top + window.pageYOffset;
      const middle = absoluteElementTop - (window.innerHeight / 2);
      
      modalBody.scrollTo({
        top: middle,
        behavior: 'smooth'
      });
      
      // Focus the element
      setTimeout(() => {
        if (elementToScroll.focus) {
          elementToScroll.focus();
        } else if (elementToScroll.querySelector('input')) {
          elementToScroll.querySelector('input').focus();
        }
      }, 500);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoader(true);

    const { isValid, firstErrorField } = validateForm();
    
    if (!isValid) {
      // Scroll to the first error field
      if (firstErrorField) {
        scrollToError(firstErrorField);
      }
      setLoader(false);
      return;
    }

    try {
      const expiryDateTimestamp = new Date(formData.expiry_date).getTime();
      const formDataToSend = new FormData();
      formDataToSend.append("title", formData.title);
      formDataToSend.append("offer_code", formData.offer_code);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("expiry_date", expiryDateTimestamp);
      formDataToSend.append("category_id", formData.category_id);
      if (formData.image) {
        formDataToSend.append("image", formData.image);
      }

      const response = await postApi("/admin/offer", formDataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.status === 200) {
        setAddOfferModal(false);
        fetchOffers();
      }
    } catch (err) {
      setError("Failed to add offer. Please try again.");
    } finally {
      setLoader(false);
    }
  };

  return (
    <div className="detail-page-modal">
      <div className="modal-header">
        <h3>Add Offer</h3>
        <button className="close-button" onClick={() => setAddOfferModal(false)}>
          <img
            src={window.getPublicUrl("/assets/images/Close_round_light.svg")}
            alt="close"
          />
        </button>
      </div>
      <div className="modal-body scrollable-modal-body add-offer-scroll">
        <div className="input-main">
          <div className="row">
            {/* Title */}
            <div className="col-sm-6" ref={titleRef}>
              <div className="custom-floating-group">
                <input
                  type="text"
                  name="title"
                  placeholder=" "
                  value={formData.title}
                  onChange={handleChange}
                  ref={titleRef}
                />
                <label>Title</label>
                {error.title && <span style={{ color: "red", fontSize: "14px" }}>{error.title}</span>}
              </div>
            </div>

            {/* Offer Code */}
            <div className="col-sm-6" ref={offerCodeRef}>
              <div className="custom-floating-group">
                <input
                  type="text"
                  name="offer_code"
                  placeholder=" "
                  value={formData.offer_code}
                  onChange={handleChange}
                />
                <label>Offer code</label>
                {error.offer_code && <span style={{ color: "red", fontSize: "14px" }}>{error.offer_code}</span>}
              </div>
            </div>

            {/* Expiry Date */}
            <div className="col-sm-6" ref={expiryDateRef}>
              <div className="custom-floating-group">
                <input
                  type="date"
                  name="expiry_date"
                  placeholder=" "
                  value={formData.expiry_date}
                  min={moment().format("YYYY-MM-DD")}
                  onChange={handleChange}
                />
                <label>Expiry date</label>
                {error.expiry_date && <span style={{ color: "red", fontSize: "14px" }}>{error.expiry_date}</span>}
              </div>
            </div>

            {/* Category Search Input */}
            <div className="col-sm-6" ref={categoryRef}>
              <div className="custom-floating-group">
                <input 
                  type="text"
                  className="form-control category" 
                  name="category_search"
                  placeholder="Search Category"
                  value={categorySearch}
                  onChange={handleCategorySearchChange}
                  onFocus={() => setIsCategoryListOpen(true)}
                />
              
                
                {isCategoryListOpen && (
                  <ul 
                    className="search-listing-ul" 
                    style={{ 
                      width: "100%", 
                      position: "absolute",
                      zIndex: 1,
                      marginTop: "5px"
                    }}
                  >
                    {filteredCategories.map((category) => (
                       <li
                       key={category.id}
                       onClick={() => handleCategorySelect(category)}
                       className={selectedCategoryId === category.id ? "active" : ""}
                   >
                       {category.name}
                   </li>
                    ))}
                    {filteredCategories.length === 0 && categorySearch && (
                      <li>No categories found</li>
                    )}
                  </ul>
                )}
                
                {error.category_id && <span style={{ color: "red", fontSize: "14px" }}>{error.category_id}</span>}
              </div>
            </div>

            {/* Description */}
            <div className="col-sm-6" ref={descriptionRef}>
              <div className="custom-floating-group position-relative height">
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder=" "
                  rows="4"
                  className={`custom-textarea ${formData.description ? "has-content" : ""}`}
                ></textarea>
                <label className="floating-label">Description</label>
                {error.description && (
                  <span style={{ color: "red", fontSize: "14px" }}>{error.description}</span>
                )}
              </div>
            </div>

            {/* Image Upload */}
            <div className="col-sm-6" ref={imageRef}>
              <div className="upload-wrapper">
                <input
                  type="file"
                  id="image"
                  name="image"
                  className="upload-input"
                  onChange={handleFileChange}
                  accept="image/*"
                />
                <label htmlFor="image" className="upload-label">
                  <img
                    className="upload-icon"
                    src={window.getPublicUrl("/assets/images/upload.svg")}
                    alt="upload"
                  />
                  Upload Image
                  <div className="upload-box">
                    {error.image && (
                      <span style={{ color: "red", fontSize: "14px" }}>
                        {error.image}
                      </span>
                    )}
                  </div>

                  {filePreviews.image && (
                    <div className="file-preview">
                      <img src={filePreviews.image} alt="Preview" />
                    </div>
                  )}
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-footer">
        <button className="cancel" onClick={() => setAddOfferModal(false)}>
          Cancel
        </button>
        <button className="save" onClick={handleSubmit} disabled={loader}>
          {loader && <div className="spinner-border" role="status"></div>}
          Save
        </button>
      </div>
    </div>
  );
};

export default AddOfferModal;